import { useFormContext } from '@calendly/form'
import { usePrevious } from '@calendly/hooks'
import { isEmpty, isEqual, pick } from 'lodash-es'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useDispatch } from 'react-redux'

import {
  determineMembers,
  getFormValue,
  prepareBookingRangeValuesForPreview,
  prepareDescriptionValuesForPreview,
  prepareLocationValuesForPreview,
  prepareNameValuesForPreview,
  usePrepareDurationValuesForPreview,
} from 'components/event_type_edit'
import { useEventType } from 'features/event_type_provider'

import { updateEventTypePreviewData } from '../../slice'
import { useGetDefaultAvailabilityRules } from '../../utils'
import { MEMBER_PREVIEW_AVAILABILITY_ATTRIBUTES, prepareAvailabilityValuesForPreview } from './components/availability'
import { MEMBER_PREVIEW_DOUBLE_BOOKING_ATTRIBUTES } from './components/free_busy_rules'
import { prepareGroupValuesForPreview } from './components/group_event'
import { prepareHostListValuesForPreview } from './components/hosts'
import { prepareLimitsAndBuffersValuesForPreview } from './components/limits_and_buffers'
import { usePreparePaymentValuesForPreview } from './components/payment'

const prepareColorValuesForPreview = (eventType, formValues) => ({
  color: getFormValue(eventType.color, formValues.color)
})

const getMemberAttributes = (member, attributes, keys) => {
  if (!attributes) { return {} }

  const memberAttributes = attributes.find(
    ({ userId, organizationInvitationId }) => (
      (member.userId && member.userId === userId)
        || (member.organizationInvitationId && member.organizationInvitationId === organizationInvitationId)
    )
  )

  return memberAttributes ? pick(memberAttributes, keys) : {}
}

const getMembersData = (members, formValues) => {
  const {
    event_type_memberships_attributes: etmAttrs,
    doubleBookingRuleMemberships
  } = formValues

  return Object.keys(members).reduce((acc, key) => {
    const member = members[key]
    const memberAvailabilityAttributes = getMemberAttributes(member,
      etmAttrs,
      MEMBER_PREVIEW_AVAILABILITY_ATTRIBUTES
    )
    const memberDoubleBookingAttributes = getMemberAttributes(member,
      doubleBookingRuleMemberships,
      MEMBER_PREVIEW_DOUBLE_BOOKING_ATTRIBUTES
    )

    return {
      ...acc,
      [key]: {
        ...member,
        ...memberAvailabilityAttributes,
        ...memberDoubleBookingAttributes
      }
    }
  }, {})
}

const useBookingPreviewSync = () => {
  const dispatch = useDispatch()
  const updatePreviewTimeout = useRef(null)
  const eventType = useEventType()

  const defaultAvailabilityRules = useGetDefaultAvailabilityRules()
  const members = useMemo(() => determineMembers(eventType, defaultAvailabilityRules.timezone), [
    eventType, defaultAvailabilityRules?.timezone
  ])

  const { values: formValues, errors: formErrors, isValidating, dirty } = useFormContext()
  const preparePaymentValuesForPreview = usePreparePaymentValuesForPreview()
  const prepareDurationValuesForPreview = usePrepareDurationValuesForPreview()

  const isInitialRender = useRef(true)
  const previousFormValues = usePrevious(formValues)
  const previousIsValidating = usePrevious(isValidating)
  const previousEventType = usePrevious(eventType)

  const isValidatingChanged = previousIsValidating !== isValidating

  const valuesChanged = useMemo(() => (
    !isEqual(previousFormValues, formValues)
  ), [formValues, previousFormValues])

  const eventTypeChanged = useMemo(() => (
    !isEqual(eventType, previousEventType)
  ), [eventType, previousEventType])

  const isDirty = dirty || valuesChanged || isValidatingChanged || eventTypeChanged

  const updatePreviewData = useCallback(() => {
    if (isValidating || !isEmpty(formErrors)) { return }

    dispatch(
      updateEventTypePreviewData(
        {
          eventType: [
            prepareNameValuesForPreview,
            prepareDurationValuesForPreview,
            prepareDescriptionValuesForPreview,
            prepareBookingRangeValuesForPreview,
            prepareLocationValuesForPreview,
            preparePaymentValuesForPreview,
            prepareGroupValuesForPreview,
            prepareLimitsAndBuffersValuesForPreview,
            prepareColorValuesForPreview,
            prepareAvailabilityValuesForPreview,
            prepareHostListValuesForPreview
          ].reduce((acc, preparator) => ({
            ...acc, ...preparator(eventType, formValues)
          }), { ...eventType }),
          members: getMembersData(members, formValues)
        }
      )
    )
  }, [
    isValidating, formErrors, dispatch,
    prepareDurationValuesForPreview, preparePaymentValuesForPreview,
    eventType, members, formValues
  ])

  const handleUpdatePreviewData = useCallback(() => {
    if (updatePreviewTimeout.current) {
      clearTimeout(updatePreviewTimeout.current)
    }

    updatePreviewTimeout.current = setTimeout(() => {
      updatePreviewData()
    }, 300)
  }, [updatePreviewData])


  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false
      updatePreviewData()
    }

    if (isDirty) { handleUpdatePreviewData() }
  }, [isDirty, handleUpdatePreviewData, updatePreviewData])
}

export const BookingPreviewSync = () => {
  useBookingPreviewSync()

  return null
}
